import { Injectable, inject } from '@angular/core';
import { LoggerService } from './logger.service';
import { NetworkService } from './network.service';

/**
 * Centralized error handling service
 * Provides consistent error handling patterns across the application
 * Formats errors appropriately for different environments
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {
  private readonly networkService = inject(NetworkService);

  constructor(private logger: LoggerService) {}

  /**
   * Handle and log an error with context
   * @param context - Context where the error occurred (e.g., 'Login', 'API', 'Storage')
   * @param error - The error object
   * @param additionalInfo - Optional additional information
   */
  handleError(context: string, error: unknown, additionalInfo?: Record<string, any>): void {
    this.logger.error(context, error, additionalInfo);
  }

  /**
   * Handle API errors specifically
   */
  handleApiError(operation: string, error: unknown): void {
    this.handleError(`API-${operation}`, error, { operation });
  }

  /**
   * Handle storage errors specifically
   */
  handleStorageError(operation: string, key: string, error: unknown): void {
    this.handleError(`Storage-${operation}`, error, { operation, key });
  }

  /**
   * Handle authentication errors specifically
   */
  handleAuthError(operation: string, error: unknown): void {
    this.handleError(`Auth-${operation}`, error, { operation });
  }

  /**
   * Get user-friendly error message from error
   * Provides offline-aware messages when device is offline
   */
  getUserFriendlyMessage(error: unknown): string {
    // Check if device is offline and provide appropriate message
    if (this.networkService.isOffline() && this.isNetworkError(error)) {
      return 'Keine Internetverbindung. Die App läuft im Offline-Modus.';
    }

    if (error instanceof Error) {
      // Check for common network error patterns
      const errorMessage = error.message.toLowerCase();
      if (errorMessage.includes('network') || 
          errorMessage.includes('offline') ||
          errorMessage.includes('failed to fetch') ||
          errorMessage.includes('networkerror') ||
          errorMessage.includes('network request failed')) {
        if (this.networkService.isOffline()) {
          return 'Keine Internetverbindung. Die App läuft im Offline-Modus.';
        }
        return 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.';
      }
      
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    } else if (typeof error === 'object' && error !== null) {
      // Try to extract message from error object
      const errorObj = error as Record<string, any>;
      if (errorObj['message']) {
        return String(errorObj['message']);
      }
      if (errorObj['error'] && typeof errorObj['error'] === 'object' && errorObj['error']['message']) {
        return String(errorObj['error']['message']);
      }
    }
    return 'An unexpected error occurred';
  }

  /**
   * Check if error is a network error
   * Enhanced detection for various network error patterns
   */
  isNetworkError(error: unknown): boolean {
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();
      return errorMessage.includes('network') || 
             errorMessage.includes('networkerror') ||
             errorMessage.includes('fetch') ||
             errorMessage.includes('http') ||
             errorMessage.includes('offline') ||
             errorMessage.includes('failed to fetch') ||
             errorMessage.includes('network request failed') ||
             errorMessage.includes('connection') ||
             errorMessage.includes('timeout') ||
             error.name === 'NetworkError' ||
             error.name === 'TypeError'; // fetch() throws TypeError on network failures
    }
    
    // Check for HttpErrorResponse (Angular HttpClient)
    if (error && typeof error === 'object') {
      const errorObj = error as Record<string, any>;
      if (errorObj['status'] === 0 || // Status 0 usually means network error
          errorObj['statusText'] === 'Unknown Error' ||
          (errorObj['error'] && errorObj['error'] instanceof ErrorEvent)) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Check if error is an offline error (device is offline)
   */
  isOfflineError(error: unknown): boolean {
    return this.networkService.isOffline() && this.isNetworkError(error);
  }

  /**
   * Check if error is a quota exceeded error
   */
  isQuotaExceededError(error: unknown): boolean {
    if (error instanceof DOMException) {
      return error.name === 'QuotaExceededError';
    }
    if (error instanceof Error) {
      return error.message.includes('quota') || error.message.includes('Quota');
    }
    return false;
  }
}
